import {
  useCreateTaskState,
  useDocEngine,
  useGetDevice,
  useGetDocumentsTemplates,
  useGetUser,
  useUpdateTaskState,
} from "@hooks";
import {
  Assignment,
  Description,
  FactCheckOutlined,
  HistoryEdu,
  MoreVert as MoreVertIcon,
  School,
} from "@mui/icons-material";
import {
  Box,
  Card,
  Chip,
  ListItemButton,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DocumentStatus } from "@types";
import dayjs, { Dayjs } from "dayjs";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getOrgTasksQueryKey } from "../../config";
import { ROUTE_SLUGS, ROUTES } from "../../config/navigation/routes";
import { UnifiedTask } from "../../services";
import { manualTaskStatusDictionary } from "../../utils/documents";
import { fillRouteWithSlugs } from "../../utils/navigation";
import {
  getTaskStatusInfo,
  isTaskManualStatus,
  normalizeTaskManualStatus,
} from "../../utils/tasks";
import { AssigneeButton } from "./AssigneeButton";
import { DeadlinePicker } from "./DeadlinePicker";
import { TaskDueDateChip } from "./TaskDueDateChip";
import { TaskIconBadge } from "./TaskIconBadge";
import { TASK_ICON_COLORS } from "./taskIconColors";

type Props = {
  task: UnifiedTask;
  showAssignee?: boolean;
  fallbackDeviceId?: string;
  horizontalPadding?: number;
  rowPadding?: TaskCardRowPadding;
  taskChipDisplay?: TaskChipDisplay;
};

export type TaskChipDisplay = "full" | "iconOnly";
export type TaskCardRowPadding = {
  paddingTop: string;
  paddingRight: string;
  paddingBottom: string;
  paddingLeft: string;
};

type TaskCircleStackItemProps = {
  children: ReactNode;
  index: number;
  total: number;
  testId: string;
};

const TaskCircleStackItem = ({
  children,
  index,
  total,
  testId,
}: TaskCircleStackItemProps) => (
  <Box
    component="span"
    data-testid={testId}
    className={[
      "relative inline-flex rounded-full bg-white p-0.5",
      index > 0 ? "-ml-1.5" : "",
    ]
      .filter(Boolean)
      .join(" ")}
    sx={{ zIndex: total - index }}
  >
    {children}
  </Box>
);

const hasValidTaskDueDate = (dueDate: UnifiedTask["dueDate"]) =>
  Boolean(dueDate) && Number.isFinite(new Date(dueDate as string).getTime());

const getTaskIcon = (taskType: UnifiedTask["taskType"]) => {
  const colors = TASK_ICON_COLORS[taskType] ?? TASK_ICON_COLORS.MANUAL;

  let icon = <Assignment />;
  switch (taskType) {
    case "MANUAL":
      icon = <Assignment />;
      break;
    case "DOCUMENT":
      icon = <Description />;
      break;
    case "APPROVAL":
      icon = <HistoryEdu />;
      break;
    case "QUALITY_EVENT":
      icon = <FactCheckOutlined />;
      break;
    case "TRAINING":
      icon = <School />;
      break;
    default:
      icon = <Assignment />;
  }

  return (
    <TaskIconBadge
      icon={icon}
      color={colors.icon}
      backgroundColor={colors.background}
    />
  );
};

export const TaskCard = ({
  task,
  showAssignee = true,
  fallbackDeviceId,
  horizontalPadding = 2,
  rowPadding,
  taskChipDisplay = "full",
}: Props) => {
  const navigate = useNavigate();
  const { orgId = "", deviceId: routeDeviceId = "" } = useParams<{
    orgId: string;
    deviceId: string;
  }>();
  const [searchParams] = useSearchParams();
  const contextDeviceIdParam = searchParams.get("deviceId") ?? "";
  const contextDeviceId =
    contextDeviceIdParam === "all" ? "" : contextDeviceIdParam;
  const queryClient = useQueryClient();

  // Device ID for mutations - use task's device or empty string for org-level tasks
  const taskStateDeviceId = task.device?.id || "";
  const deviceIdForDocEngine =
    task.taskType === "QUALITY_EVENT"
      ? ""
      : task.device?.id ||
        contextDeviceId ||
        routeDeviceId ||
        fallbackDeviceId ||
        "";
  const navigationDeviceId = deviceIdForDocEngine;
  const canUseDocEngine =
    Boolean(deviceIdForDocEngine) &&
    (task.taskType === "DOCUMENT" || task.taskType === "APPROVAL");

  const { data: user } = useGetUser();
  const { data: device } = useGetDevice({
    orgId,
    deviceId: canUseDocEngine ? deviceIdForDocEngine : "",
  });
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId: deviceIdForDocEngine,
    enabled: canUseDocEngine && Boolean(orgId),
  });
  const docEngine = useDocEngine(deviceIdForDocEngine);

  const createTaskState = useCreateTaskState({
    orgId,
    deviceId: taskStateDeviceId,
  });
  const updateTaskState = useUpdateTaskState({
    orgId,
    deviceId: taskStateDeviceId,
  });

  const manualStatus = useMemo(
    () => normalizeTaskManualStatus(task.status),
    [task.status]
  );
  const documentStatus =
    task.taskType === "DOCUMENT"
      ? (task.docStatus ?? DocumentStatus.AVAILABLE)
      : DocumentStatus.AVAILABLE;
  const taskStatusInfo =
    task.taskType === "DOCUMENT"
      ? getTaskStatusInfo({
          taskType: "DOCUMENT",
          status: documentStatus,
        })
      : getTaskStatusInfo({
          taskType: task.taskType,
          status: manualStatus,
        });
  const isManualTask = task.taskType === "MANUAL";
  const isApprovalTypeTask =
    task.taskType === "APPROVAL" || task.taskType === "QUALITY_EVENT";
  const isDeadlineEditableTask =
    task.taskType === "MANUAL" || task.taskType === "DOCUMENT";
  const isAssigneeDisabled = isApprovalTypeTask;
  const isIconOnlyChipDisplay = taskChipDisplay === "iconOnly";

  // Status menu state
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [deadlineMenuAnchorEl, setDeadlineMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [deadlinePickerValue, setDeadlinePickerValue] = useState<Dayjs | null>(
    task.dueDate ? dayjs(task.dueDate) : null
  );

  useEffect(() => {
    setDeadlinePickerValue(task.dueDate ? dayjs(task.dueDate) : null);
  }, [task.dueDate]);

  const handleStatusChipClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (isManualTask) {
      setStatusMenuAnchorEl(event.currentTarget as HTMLElement);
    }
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchorEl(null);
  };

  const handleDeadlineMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setDeadlineMenuAnchorEl(event.currentTarget);
  };

  const handleDeadlineMenuClose = () => {
    setDeadlineMenuAnchorEl(null);
  };

  const handleStatusChange = async (
    event: React.MouseEvent,
    newStatus: string
  ) => {
    event.stopPropagation();
    event.preventDefault();
    handleStatusMenuClose();

    if (!isTaskManualStatus(newStatus) || newStatus === manualStatus) return;

    if (task.id) {
      await updateTaskState.mutateAsync({
        stateId: task.id,
        data: { manualStatus: newStatus },
      });
    } else if (task.taskKey) {
      await createTaskState.mutateAsync({
        taskKey: task.taskKey,
        manualStatus: newStatus,
      });
    }

    await queryClient.invalidateQueries({
      queryKey: getOrgTasksQueryKey(orgId, "all"),
    });
    await queryClient.invalidateQueries({
      queryKey: getOrgTasksQueryKey(orgId, "mine"),
    });
  };

  const handleAssigneeChange = async (assigneeId: string | null) => {
    // If task has an ID (state exists), update it
    if (task.id) {
      await updateTaskState.mutateAsync({
        stateId: task.id,
        data: { assigneeId },
      });
    } else if (task.taskKey) {
      // Create a new task state with the assignee
      await createTaskState.mutateAsync({
        taskKey: task.taskKey,
        assigneeId,
      });
    }
    // Invalidate org tasks queries to refresh the list
    await queryClient.invalidateQueries({
      queryKey: getOrgTasksQueryKey(orgId, "all"),
    });
    await queryClient.invalidateQueries({
      queryKey: getOrgTasksQueryKey(orgId, "mine"),
    });
  };

  const mutateDueDate = async (dueDate: string | null) => {
    if (task.id) {
      await updateTaskState.mutateAsync({
        stateId: task.id,
        data: { dueDate },
      });
    } else if (task.taskKey) {
      await createTaskState.mutateAsync({
        taskKey: task.taskKey,
        dueDate,
      });
    } else {
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: getOrgTasksQueryKey(orgId, "all"),
    });
    await queryClient.invalidateQueries({
      queryKey: getOrgTasksQueryKey(orgId, "mine"),
    });
  };

  const handleDeadlineChange = async (newDate: Dayjs | null) => {
    if (!isDeadlineEditableTask) return;
    setDeadlinePickerValue(newDate);
    handleDeadlineMenuClose();
    await mutateDueDate(newDate?.toISOString() ?? null);
  };

  const buildDetailUrl = (path: string) => {
    if (!contextDeviceId) {
      return path;
    }
    const params = new URLSearchParams();
    params.set("returnTo", "device");
    params.set("deviceId", contextDeviceId);
    return `${path}?${params.toString()}`;
  };

  const handleClick = () => {
    if (task.taskType === "QUALITY_EVENT" && task.actionPath) {
      navigate(task.actionPath);
      return;
    }
    // Navigation logic based on task type
    if (task.taskType === "MANUAL" || task.taskType === "TRAINING") {
      const taskIdentifier = task.id ?? task.taskKey;
      // TRAINING tasks are always organization-scoped, so they always route
      // through ORG_TASK_DETAIL.
      const targetDeviceId =
        task.taskType === "TRAINING" ? "" : task.device?.id || routeDeviceId;
      const targetRoute = targetDeviceId
        ? ROUTES.TASK_DETAIL
        : ROUTES.ORG_TASK_DETAIL;

      // Navigate to task detail view
      const path = fillRouteWithSlugs(targetRoute, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
        ...(targetDeviceId && { [ROUTE_SLUGS.DEVICE_ID]: targetDeviceId }),
        [ROUTE_SLUGS.TASK_ID]: taskIdentifier,
      });
      navigate(buildDetailUrl(path), {
        state: contextDeviceId
          ? { returnTo: "device", deviceId: contextDeviceId }
          : undefined,
      });
    }

    if (
      (task.taskType === "DOCUMENT" || task.taskType === "APPROVAL") &&
      (task.templateKey || task.documentId)
    ) {
      if (canUseDocEngine && device && user && templatesConfig && task.templateKey) {
        void docEngine.navigateToWizardOrQMS({
          templateKey: task.templateKey,
          documentId: task.documentId ?? undefined,
        });
        return;
      }

      if (navigationDeviceId && task.documentId) {
        const path = fillRouteWithSlugs(ROUTES.QMS_OPEN_DOC, {
          [ROUTE_SLUGS.ORG_ID]: orgId,
          [ROUTE_SLUGS.DEVICE_ID]: navigationDeviceId,
          [ROUTE_SLUGS.DOC_ID]: task.documentId,
        });
        navigate(path);
      }
    }
  };

  const dueDateFormat = "MM/DD/YYYY";
  const dueDatePlaceholder = "No due date";
  const dueDateInputSize =
    (deadlinePickerValue
      ? deadlinePickerValue.format(dueDateFormat).length
      : dueDatePlaceholder.length) + 1;
  const compactDateSx = {
    width: "auto",
    minWidth: 0,
    "& .MuiInput-underline:before": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:after": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottom: "none",
    },
    "& .MuiInputBase-root": {
      width: "auto",
      minWidth: 0,
      px: 1,
      py: 0.5,
      borderRadius: 1,
      display: "inline-flex",
      "&:hover": {
        backgroundColor: "action.hover",
      },
    },
    "& .MuiInputBase-input": {
      lineHeight: "20px",
      p: 0,
      width: "auto",
      minWidth: 0,
    },
    "& .MuiInputAdornment-root": {
      ml: 0.5,
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity 150ms ease",
    },
    "& .MuiInputBase-root:hover .MuiInputAdornment-root, & .MuiInputBase-root.Mui-focused .MuiInputAdornment-root":
      {
        opacity: 1,
        pointerEvents: "auto",
      },
  } as const;

  const statusCircle = (
    <Tooltip title={taskStatusInfo.label} placement="top">
      <Box
        component="span"
        data-testid="task-status-chip"
        onClick={isManualTask ? handleStatusChipClick : undefined}
        onMouseDown={
          isManualTask
            ? (e: React.MouseEvent) => e.stopPropagation()
            : undefined
        }
        sx={{
          alignItems: "center",
          backgroundColor: "inherit",
          border: 1,
          borderColor: taskStatusInfo.borderColor,
          borderRadius: "50%",
          cursor: isManualTask ? "pointer" : "default",
          display: "inline-flex",
          height: 24,
          justifyContent: "center",
          width: 24,
          "& .MuiSvgIcon-root": {
            fontSize: 18,
          },
          "&:hover": isManualTask
            ? {
                backgroundColor: "action.hover",
              }
            : undefined,
        }}
      >
        {taskStatusInfo.icon}
      </Box>
    </Tooltip>
  );

  const statusChip = (
    <Chip
      data-testid="task-status-chip"
      icon={taskStatusInfo.icon}
      label={taskStatusInfo.label}
      size="small"
      variant="outlined"
      onClick={isManualTask ? handleStatusChipClick : undefined}
      onMouseDown={
        isManualTask ? (e: React.MouseEvent) => e.stopPropagation() : undefined
      }
      sx={{
        height: 24,
        borderColor: taskStatusInfo.borderColor,
        ...(isManualTask && { cursor: "pointer" }),
      }}
    />
  );

  const dueDateChip = (
    <TaskDueDateChip dueDate={task.dueDate} iconOnly={isIconOnlyChipDisplay} />
  );
  const assigneeButton = (
    <AssigneeButton
      assignee={task.assignee}
      onAssigneeChange={handleAssigneeChange}
      disabled={isAssigneeDisabled}
    />
  );
  const taskCircleStackItems: Array<{ testId: string; content: ReactNode }> = [
    {
      testId: "task-circle-stack-status",
      content: statusCircle,
    },
    ...(hasValidTaskDueDate(task.dueDate)
      ? [
          {
            testId: "task-circle-stack-due-date",
            content: dueDateChip,
          },
        ]
      : []),
    ...(showAssignee
      ? [
          {
            testId: "task-circle-stack-assignee",
            content: assigneeButton,
          },
        ]
      : []),
  ];

  return (
    <Card
      sx={{
        borderRadius: "5px",
        boxShadow: "none",
        backgroundColor: "transparent",
        overflow: "hidden",
      }}
    >
      <ListItemButton
        component="button"
        onClick={handleClick}
        disableGutters
        sx={{
          m: 0,
          width: "100%",
          ...(rowPadding ?? { px: horizontalPadding, py: 0 }),
          borderRadius: "inherit",
          height: 44,
          minHeight: 44,
          backgroundColor: "transparent",
        }}
      >
        <Box className="flex w-full items-center gap-3">
          <Box className="flex-shrink-0">{getTaskIcon(task.taskType)}</Box>
          <Box className="flex-1 min-w-0">
            <Typography variant="subtitle1" noWrap className="font-medium">
              {task.title}
            </Typography>
          </Box>

          {/* Right side: Status, Due Date, Assignee */}
          <Box className="flex items-center gap-2 flex-shrink-0">
            {isIconOnlyChipDisplay ? (
              <Box
                component="span"
                data-testid="task-circle-stack"
                className="flex items-center"
              >
                {taskCircleStackItems.map((item, index) => (
                  <TaskCircleStackItem
                    key={item.testId}
                    index={index}
                    total={taskCircleStackItems.length}
                    testId={item.testId}
                  >
                    {item.content}
                  </TaskCircleStackItem>
                ))}
              </Box>
            ) : (
              statusChip
            )}
            {isManualTask && (
              <Menu
                anchorEl={statusMenuAnchorEl}
                open={Boolean(statusMenuAnchorEl)}
                onClose={handleStatusMenuClose}
                disablePortal
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                BackdropProps={{
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    handleStatusMenuClose();
                  },
                  onMouseDown: (e: React.MouseEvent) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                  },
                }}
                PaperProps={{
                  sx: { minWidth: "max-content" },
                }}
                MenuListProps={{
                  sx: { whiteSpace: "nowrap" },
                }}
              >
                {Object.entries(manualTaskStatusDictionary).map(
                  ([statusKey, statusInfo]) => (
                    <MenuItem
                      key={statusKey}
                      selected={statusKey === manualStatus}
                      onClick={(e) => handleStatusChange(e, statusKey)}
                    >
                      <Box className="flex items-center gap-1">
                        <span className="flex items-center leading-none">
                          {statusInfo.icon}
                        </span>
                        <span className="leading-none">{statusInfo.label}</span>
                      </Box>
                    </MenuItem>
                  )
                )}
              </Menu>
            )}
            {!isIconOnlyChipDisplay && dueDateChip}
            {!isIconOnlyChipDisplay && showAssignee && assigneeButton}
            {showAssignee && (
              <Box
                component="span"
                aria-label="Task deadline options"
                data-testid="task-deadline-more-button"
                onClick={handleDeadlineMenuOpen}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  cursor: "pointer",
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <MoreVertIcon fontSize="small" />
              </Box>
            )}
          </Box>
        </Box>
      </ListItemButton>
      <Menu
        anchorEl={deadlineMenuAnchorEl}
        open={Boolean(deadlineMenuAnchorEl)}
        onClose={handleDeadlineMenuClose}
        onClick={(event) => {
          event.stopPropagation();
        }}
        disablePortal
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        BackdropProps={{
          onClick: (event: React.MouseEvent) => {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
            handleDeadlineMenuClose();
          },
          onMouseDown: (event: React.MouseEvent) => {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
          },
        }}
      >
        <ListSubheader>Deadline</ListSubheader>
        <Box
          sx={{ px: 2, pb: 1 }}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <Tooltip
            title={
              isApprovalTypeTask
                ? "Deadline cannot be set for approval tasks"
                : ""
            }
            disableHoverListener={!isApprovalTypeTask}
            placement="top"
          >
            <Box
              component="span"
              data-testid="task-deadline-picker-tooltip-trigger"
            >
              <DeadlinePicker
                value={deadlinePickerValue}
                disabled={!isDeadlineEditableTask}
                onChange={(newDate) => {
                  void handleDeadlineChange(newDate);
                }}
                onClear={() => {
                  if (!isDeadlineEditableTask) return;
                  setDeadlinePickerValue(null);
                  void mutateDueDate(null);
                  handleDeadlineMenuClose();
                }}
                ariaLabel="Task card due date"
                format={dueDateFormat}
                placeholder={dueDatePlaceholder}
                textFieldProps={{
                  variant: "standard",
                  hiddenLabel: true,
                  inputProps: {
                    size: Math.max(dueDateInputSize, 1),
                  },
                  InputProps: {
                    disableUnderline: true,
                  },
                  onClick: (event) => event.stopPropagation(),
                  sx: compactDateSx,
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      </Menu>
    </Card>
  );
};
